import {
  type ChangeEvent,
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { membershipSchema } from "../schemas";
import { Memberships } from "../../../types/Types";

const CONTAINER_MIN_HEIGHT = 400;
const CONTAINER_STYLE = { minHeight: CONTAINER_MIN_HEIGHT };
const CARD_CLICKABLE_STYLE = { cursor: "pointer" as const };
const STRIKETHROUGH_STYLE = {
  textDecoration: "line-through" as const,
  marginRight: "8px",
  color: "grey",
};
const DISCOUNT_CODES: { code: string; description: string }[] = [
  { code: "24OFF", description: "24 month free" },
  { code: "DISCOUNT12", description: "12 month free" },
  { code: "WAIVE6", description: "6 month free" },
  { code: "COMP3", description: "3 month free" },
  { code: "1COMP", description: "1 month free" },
  { code: "53OFF", description: "50% off for 3 months" },
  { code: "56OFF", description: "50% off for 6 months" },
  { code: "512OFF", description: "50% off for 12 months" },
];

function debounce<A extends unknown[]>(
  func: (...args: A) => void,
  delay: number
): (...args: A) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: A) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
}

interface MembershipData {
  membershipName: string;
  isAnnualMembership: number;
}

interface MembershipStepProps {
  defaultValues: MembershipData;
  memberships: Memberships[];
  onNext: (data: MembershipData, price: number) => void;
  onBack: () => void;
  setMembershipPrice: (price: number) => void;
  discountCode: string;
  setDiscountCode: (code: string) => void;
  /** When upgrading/renewing, pass current user membership so that option is disabled */
  currentMembershipName?: string;
}

function MembershipStepInner({
  defaultValues,
  memberships,
  setMembershipPrice,
  onNext,
  onBack,
  discountCode,
  setDiscountCode,
  currentMembershipName,
}: MembershipStepProps) {
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<MembershipData>({
    resolver: yupResolver(membershipSchema),
    defaultValues,
  });

  const [discountError, setDiscountError] = useState("");
  const [showDiscountInput, setShowDiscountInput] = useState(false);

  const membershipName = watch("membershipName");
  const isAnnualMembership = watch("isAnnualMembership");

  // Force monthly billing (no annual plans active)
  useEffect(() => {
    if (isAnnualMembership !== 0) {
      setValue("isAnnualMembership", 0);
    }
  }, [isAnnualMembership, setValue]);

  // When upgrading: if current plan is selected, default to first other plan (current plan is disabled)
  useEffect(() => {
    if (!currentMembershipName || membershipName !== currentMembershipName) return;
    const firstOther = memberships.find((m) => m.name !== currentMembershipName);
    if (firstOther) setValue("membershipName", firstOther.name);
  }, [currentMembershipName, memberships, membershipName, setValue]);

  const handleDiscountChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const code = e.target.value.trim().toUpperCase();
      setDiscountCode(code);

      if (!code) {
        setDiscountError("");
        return;
      }

      const validCode = DISCOUNT_CODES.find((dc) => dc.code === code);
      if (code === "512OFF" && watch("isAnnualMembership") !== 1) {
        setDiscountError(
          "This discount code is only applicable for annual membership"
        );
        return;
      }
      setDiscountError(validCode ? "" : "Invalid discount code");
    },
    [setDiscountCode, setDiscountError, watch]
  );

  const validateDiscountCode = useMemo(
    () => debounce(handleDiscountChange, 500),
    [handleDiscountChange]
  );

  const calculateDiscountedPrice = useCallback(
    (m: Memberships) => {
      const monthlyFee = Number(m.monthlyFee);
      const annualFee = Number(m.annualFee);
      const isAnnual = isAnnualMembership === 1;

      const price = isAnnual ? annualFee : monthlyFee;

      if (!discountCode) return price;

      let discountedPrice = price;

      switch (discountCode) {
        case "1COMP":
          discountedPrice = isAnnual ? annualFee - monthlyFee : 0;
          break;
        case "COMP3":
          discountedPrice = isAnnual ? annualFee - monthlyFee * 3 : 0;
          break;
        case "WAIVE6":
          discountedPrice = isAnnual ? annualFee - monthlyFee * 6 : 0;
          break;
        case "DISCOUNT12":
          discountedPrice = isAnnual ? 0 : 0;
          break;
        case "24OFF":
          discountedPrice = 0;
          break;
        case "53OFF":
          discountedPrice = isAnnual
            ? annualFee - monthlyFee * 0.5 * 3
            : monthlyFee * 0.5;
          break;
        case "56OFF":
          discountedPrice = isAnnual
            ? annualFee - monthlyFee * 0.5 * 6
            : monthlyFee * 0.5;
          break;
        case "512OFF":
          discountedPrice = isAnnual ? annualFee / 2 : monthlyFee * 0.5;
          break;
        default:
          discountedPrice = price;
      }

      return Math.max(discountedPrice, 0);
    },
    [isAnnualMembership, discountCode]
  );

  const onSubmit = useCallback(
    (data: MembershipData) => {
      const isCodeEmpty = discountCode.trim() === "";
      const isValidCode = DISCOUNT_CODES.some(
        (dc) => dc.code === discountCode.trim().toUpperCase()
      );

      if (
        discountCode.trim().toUpperCase() === "512OFF" &&
        isAnnualMembership !== 1
      ) {
        setDiscountError(
          "This discount code is only applicable for annual membership"
        );
        return;
      }

      if (!isCodeEmpty && !isValidCode) {
        setDiscountError("Invalid discount code");
        return;
      }
      const selectedMembership = memberships.find(
        (m) => m.name === data.membershipName
      );
      if (selectedMembership) {
        const price = calculateDiscountedPrice(selectedMembership) as number;
        setMembershipPrice(price);
        onNext({ ...data, isAnnualMembership }, price);
      }
    },
    [
      discountCode,
      isAnnualMembership,
      memberships,
      setMembershipPrice,
      onNext,
      setDiscountError,
      calculateDiscountedPrice,
    ]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-100">
      <div
        className="d-flex flex-column gap-3"
        style={CONTAINER_STYLE}
      >
        <div className="d-flex flex-column flex-md-row justify-content-between">
          <h5 className="mb-0">Select Membership</h5>
          <div className="d-flex justify-content-end align-items-end">
            <span className="small px-2 text-nowrap">Monthly / Annual</span>
            <Form.Check
              className="mb-4"
              type="switch"
              id="billing-mode"
              disabled
              checked={isAnnualMembership === 1}
              onChange={() =>
                setValue(
                  "isAnnualMembership",
                  isAnnualMembership === 0 ? 1 : 0
                )
              }
            />
          </div>
        </div>

        {memberships?.map((m) => {
          const discountedPrice = calculateDiscountedPrice(m);
          const isCurrentPlan = !!currentMembershipName && m.name === currentMembershipName;

          return (
            <div
              key={m.name}
              className={`d-flex justify-content-between align-items-center p-3 rounded-3 border ${
                membershipName === m.name
                  ? "bg-light fw-bold border-primary"
                  : "border-secondary"
              } ${isCurrentPlan ? "opacity-75" : ""}`}
              style={isCurrentPlan ? { cursor: "not-allowed", pointerEvents: "none" } : CARD_CLICKABLE_STYLE}
              onClick={isCurrentPlan ? undefined : () => setValue("membershipName", m.name)}
            >
              <div className="d-flex align-items-center gap-3">
                <input
                  type="radio"
                  name="membershipName"
                  checked={membershipName === m.name}
                  disabled={isCurrentPlan}
                  onChange={() => !isCurrentPlan && setValue("membershipName", m.name)}
                />
                <div className="d-flex align-items-center gap-2">
                  <span>{m.name.replace("Membership", "").trim()}</span>
                  {isCurrentPlan && (
                    <span className="badge bg-secondary text-nowrap">Current plan</span>
                  )}
                </div>
              </div>
              <div className="text-end">
                <span>
                  £{Math.max(Number(discountedPrice), 0).toFixed(2)}/Month
                </span>
              </div>
            </div>
          );
        })}

        {errors.membershipName && (
          <p className="custom-error-text mb-0 px-1 text-danger">
            {errors.membershipName.message}
          </p>
        )}
      </div>

      <div className="mt-4">
      {!showDiscountInput ? (
        <button
          type="button"
          className="btn btn-link p-0 text-start border-0 bg-transparent text-primary text-decoration-underline"
          onClick={() => setShowDiscountInput(true)}
        >
          Have a discount code?
        </button>
      ) : (
        <Form.Group>
          <Form.Label>Discount Code</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter discount code"
            onChange={validateDiscountCode}
          />
          {discountError && (
            <Form.Text className="text-danger">{discountError}</Form.Text>
          )}
        </Form.Group>
      )}
    </div>

      <div className="d-flex justify-content-between mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onBack}
        >
          Back
        </button>
        <button type="submit" className="btn btn-primary">
          Next
        </button>
      </div>
    </form>
  );
}

MembershipStepInner.displayName = "MembershipStep";

const MembershipStep = memo(MembershipStepInner);
MembershipStep.displayName = "MembershipStep";

export default MembershipStep;
